import { Component, EventEmitter, Output, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import * as _ from 'lodash';

import { Option } from 'shared/form/select/option.interface';
import { createOption } from 'shared/form/select/option-helpers';

import { BaseModalComponent } from 'shared/modal/_base-modal.component';
import { ConfigureExportNameComponent } from '../configure-export-name/configure-export-name.component';
import { AdvancedExportOptionsComponent } from '../advanced-export-options/advanced-export-options.component';
import { ExportPriceSheetOptionsComponent } from '../export-price-sheet-options/export-price-sheet-options.component';

import { ExportManagerService } from 'shared/export-manager/export-manager.service';
import { BrandService } from 'shared/brand/brand.service';
import {map, takeWhile} from 'rxjs/operators';
import {ProductPanelComponent} from '../../../../product-panel/product-panel.component';


@Component({
  template: '',
})
export class BaseConfigureExportModalComponent extends BaseModalComponent {
  status: string = ''; // published/working/etc

  advOptionsCollapsed: boolean = true;
  // formats
  exportTypesList: Array<any> = [
    {
      title: 'Item Format',
      typeId: 100, // id for grouping
      options: [],
    },
    {
      title: 'Fitment Format',
      typeId: 101,
      options: [],
    },
    {
      title: 'Digital Assets Format',
      typeId: 102,
      options: [],
    },
  ];
  hasExportTypes: boolean = false;

  @ViewChild(ConfigureExportNameComponent, {static: false})
  configureExportName: ConfigureExportNameComponent | void;

  @ViewChild(AdvancedExportOptionsComponent, {static: false})
  advancedOptions: AdvancedExportOptionsComponent | void;

  @ViewChild(ExportPriceSheetOptionsComponent, {static: false})
  priceSheetOptions: ExportPriceSheetOptionsComponent | void;

  @ViewChild('scrollEl', {static: false})
  scrollEl: ElementRef | void;

  @ViewChild('collapseBtnEl', { read: ElementRef, static: false })
  collapseBtnEl: ElementRef | void;

  @Output() complete: EventEmitter<any> = new EventEmitter();

  @Output() scheduleExport: EventEmitter<any> = new EventEmitter();

  constructor(
    protected ref: ChangeDetectorRef,
    protected exportManagerService: ExportManagerService,
    protected brandService: BrandService,
  ) {
    super();
  }

  /*******************************************
  * COMPONENT GETTERS
  *******************************************/

  get selectedPrices(): Array<string|number> {
    return _.get(this.priceSheetOptions, 'selectedPrices');
  }

  get selectedCurrencies(): Array<string|number> {
    return _.get(this.priceSheetOptions, 'selectedCurrencies');
  }

  get selectedCustomFields(): Array<string|number> {
    return _.get(this.advancedOptions, 'selectedCustomFields');
  }

  get selectedInterchange(): Array<string|number> {
    return _.get(this.advancedOptions, 'selectedInterchange');
  }

  get selectedFitmentStatus(): Array<string|number> {
    return _.get(this.advancedOptions, 'selectedFitmentStatus');
  }

  get canContinue(): boolean {
    if (this.hasExportTypes) {
      return  _.size(this.selectedPrices) > 0 &&
              _.some(this.exportTypesList, (item: any) => {
                  return !!_.find(item.options, ['selected', true]);
              });
    } else {
      return _.size(this.selectedPrices) > 0;
    }
  }

  get fileName(): string {
    return _.get(this.configureExportName, 'nameValue');
  }

  get selectedExportTypeIds(): Array<number|string> {
    return _.reduce(this.exportTypesList, (result: Array<string|number>, item: any) => {
      return _.concat(result, this.getSelectedOptions(item.options));
    }, []);
  }

  get brandId(): string|number {
    return this.brandService.getBrand();
  }

  /*******************************************
  * COMPONENT HANDLERS
  *******************************************/

  onShow(event: any) {
    if (this.advancedOptions) {
      this.advancedOptions.getLists();
    }
    if (this.priceSheetOptions) {
      this.priceSheetOptions.getLists();
    }
    if (this.configureExportName) {
      this.configureExportName.init();
    }
    if (this.hasExportTypes) {
      this.getExportTypes();
    }
  }

  onHide(event: any) {
    if (this.advancedOptions) {
      this.advancedOptions.reset();
    }
    // reset formats selected options
    if (this.hasExportTypes) {
      this.resetFormats();
    }
    this.advOptionsCollapsed = true;
    if (this.configureExportName) {
      this.configureExportName.resetControl();
    }
    this.ref.markForCheck();
  }

  onCompleteClick() {
    this.complete.emit(this.getConfigurationData());
  }

  onScheduleExportClick() {
    this.scheduleExport.emit(this.getConfigurationData());
  }

  onCollapseBtnClick() {
    this.advOptionsCollapsed = !this.advOptionsCollapsed;
    if (!this.advOptionsCollapsed) {
      // scroll to the advanced options html section
      _.delay(() => {
        if (this.scrollEl) {
          this.scrollEl.nativeElement.scrollTop = _.get(this.collapseBtnEl, 'nativeElement.offsetTop');
        }
      }, 200);
    }
  }

  /*******************************************
  * COMPONENT PROTECTED
  *******************************************/

  protected getConfigurationData(): any {
    return {
      priceSheets: this.selectedPrices,
      customFields: this.selectedCustomFields,
      userFileName: this.fileName,
      exportTypeIds: this.selectedExportTypeIds,
      interchange: this.selectedInterchange,
      fitmentStatus: this.selectedFitmentStatus,
    };
  }

  protected resetFormats(): void {
    if (!this.hasExportTypes) { return; }
    _.each(this.exportTypesList, (item: any) => {
      item.options = [];
    });
  }

  protected getSelectedOptions(list: Array<Option>): Array<number|string> {
    return _.chain(list)
    .filter(['selected', true])
    .map('option')
    .value();
  }

  protected getExportTypes(): void {
    this.exportManagerService.getExportTypes(this.brandId)
    .pipe(takeWhile(() => this.isActive), map((types: Array<any>) => {
        // group by type_id and create option for every item in the data
        return _.reduce(types, (result: any, item: any) => {
            if (!result[item.type_id]) { result[item.type_id] = []; }
            result[item.type_id].push(createOption(item.id, item.name));
            return result;
        }, {});
    }))
    .subscribe((types: Array<any>) => {
      this.setExportTypes(types);
    });
  }

  /*
    *  Set grouped data to the exportTypesList options
    *  Preselect first option of the first format
    *  @param {type_id: Array<{option, title}>} groupedData
   */
  protected setExportTypes(groupedData: any): void {
    _.each(this.exportTypesList, (item: any, index: number) => {
      _.set(item, 'options', groupedData[item.typeId]);
    });
    _.set(this.exportTypesList[0], 'options[0].selected', true);
    this.ref.markForCheck();
  }
}
